import { useEffect, useRef, useCallback } from 'react';
import { useToast, Box } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useGetNotifications, NotificationItem, NotificationType } from '../../hooks/useGetNotifications';
import { useMarkNotificationRead } from '../../hooks/useMarkNotificationRead';
import { useMarkNegotiationRead } from '../../hooks/invoice/useInvoiceNegotiation';
import { useOpenNotificationTarget } from '../../hooks/useOpenNotificationTarget';
import { invoicesListHref } from '../../shared/invoicesListViewState';
import NotificationToast from './NotificationToast';

// Map notification type to order page tab
const getInitialStatusForNotification = (type: NotificationType): 'newParts' | 'quoting' | 'selection' | 'ordered' | 'rekey' => {
    switch (type) {
        case 'new-order':
            return 'newParts';
        case 'quotes-extended':
        case 'auto-requote':
            return 'quoting';
        case 'quotes-back':
        case 'quotes-back-potential-total':
        case 'quotes-back-quotes-only':
        case 'no-quoted-parts':
        case 'flagged-parts':
        case 'part-number-changed':
        case 'order-request-enabled':
            return 'selection';
        case 'orders-confirmed':
        case 'orders-rejected':
        case 'orders-returned-cancelled':
        case 'tesla-order-error':
            return 'ordered';
        case 'rekeying-completed':
        case 'rekeying-completed-flagged':
        case 'rekeying-skipped':
        case 'rekey-quote-only-completed':
            return 'rekey';
        case 'needs-rekey':
        case 'rekey-advisor-score':
            return 'rekey';
        case 'flagged-invoices':
            return 'newParts';
        case 'order-placement-failed':
        case 'ccc-manual-po-created':
        case 'ccc-manual-po-failed':
            return 'ordered';
        default:
            return 'newParts';
    }
};

interface NotificationToastManagerProps {
    onOpenEmsModal?: (emsId: string) => void;
}

const NotificationToastManager = ({ onOpenEmsModal }: NotificationToastManagerProps) => {
    const toast = useToast();
    const navigate = useNavigate();
    const openNotificationTarget = useOpenNotificationTarget();
    const markAsRead = useMarkNotificationRead();
    const markNegotiationRead = useMarkNegotiationRead();
    const seenNotificationIds = useRef<Set<string>>(new Set());
    const isInitialLoad = useRef(true);
    
    const { data } = useGetNotifications(true);

    const handleNotificationClick = useCallback((notification: NotificationItem, toastId: string | number) => {
        // Close the toast
        toast.close(toastId);

        if (notification.data.type === 'negotiation') {
            markNegotiationRead.mutate(notification.data.negotiationId);
            navigate(
                invoicesListHref({
                    type: 'all',
                    showArchived: false,
                    showNegotiation: true,
                    selectedNegotiationId: notification.data.negotiationId,
                    currentPage: 1,
                }),
                { state: { source: 'notifications' } },
            );
            return;
        }

        // Mark as read
        if (!notification.read) {
            markAsRead.mutate({ notificationId: notification._id });
        }

        // Navigate based on notification type
        if (notification.data.type === 'ems') {
            if (onOpenEmsModal) {
                onOpenEmsModal(notification.data.emsId);
            }
        } else if (notification.data.type === 'invoice') {
            navigate(`/invoices/${notification.data.invoiceId}`, {
                state: { source: 'notifications' },
            });
        } else {
            const initialStatus = getInitialStatusForNotification(notification.type);
            openNotificationTarget(notification, `/order/${notification.data.orderId}`, {
                source: 'notifications',
                initialStatus,
            });
        }
    }, [toast, markAsRead, markNegotiationRead, navigate, onOpenEmsModal, openNotificationTarget]);

    useEffect(() => {
        if (!data?.notifications) {
            return;
        }

        const enabledTypes = data.enabledNotificationTypes;
        const globalEnabled = data.notificationsEnabled;
        
        // On initial load, just mark all existing notifications as seen (don't show toasts)
        if (isInitialLoad.current) {
            data.notifications.forEach(notification => {
                seenNotificationIds.current.add(notification._id);
            });
            isInitialLoad.current = false;
            return;
        }

        // Find new notifications that we haven't seen yet
        const newNotifications = data.notifications.filter(notification => {
            // Skip if already seen
            if (seenNotificationIds.current.has(notification._id)) {
                return false;
            }

            // Skip all other types if notifications are globally disabled
            if (!globalEnabled) {
                return false;
            }

            // Skip if notification type is not enabled
            // If enabledTypes is undefined or empty object, all types are enabled
            if (enabledTypes && Object.keys(enabledTypes).length > 0) {
                const channels = enabledTypes[notification.type];
                if (channels !== undefined && !channels.includes('notif')) {
                    return false;
                }
            }
            
            return true;
        });

        // Show toasts for new notifications (limit to 3 at a time to avoid overwhelming)
        const toastsToShow = newNotifications.slice(0, 3);
        
        toastsToShow.forEach((notification, index) => {
            // Mark as seen immediately
            seenNotificationIds.current.add(notification._id);
            
            // Stagger the toasts slightly for better UX
            setTimeout(() => {
                const toastId = `notification-${notification._id}`;
                
                toast({
                    id: toastId,
                    position: 'top-right',
                    duration: 6000,
                    isClosable: true,
                    render: ({ onClose }) => (
                        <Box mt={2}>
                            <NotificationToast
                                notification={notification}
                                onClose={onClose}
                                onClick={() => handleNotificationClick(notification, toastId)}
                            />
                        </Box>
                    ),
                });
            }, index * 200);
        });

        // Mark remaining new notifications as seen (without showing toasts)
        newNotifications.slice(3).forEach(notification => {
            seenNotificationIds.current.add(notification._id);
        });
    }, [data?.notifications, data?.notificationsEnabled, data?.enabledNotificationTypes, toast, handleNotificationClick]);

    return null;
};

export default NotificationToastManager;
